import { Stack, Text, Group } from '@mantine/core';
import Card from 'components/Card';
import DataTables from 'components/DataTables';
import Toolbar from 'components/Toolbar';
import React, { useState, useEffect } from 'react';
import { columns as COLUMN_JOB_FAMILY } from './schemaJobFamily';
import { useDisclosure } from '@mantine/hooks';
import { IconPlus } from '@tabler/icons-react';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { pathnames } from 'routes/dacum';
import { useSchoolManagementStore } from 'store/schoolManagement';
import { SchoolJobDataFamily } from 'types/dacum/schoolManagement';
import ModalDelete from 'modules/pages/lcms/courses/ModalDelete';
import ModalPopup from 'components/ModalPopup';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { schoolService } from 'services/dacum/school.service';
import { showErrorNotification, showSuccessNotification } from 'utils/notification';
import { HiOutlineDocumentText } from 'react-icons/hi';
import Dates from 'components/Dates';

interface JobFamilyParams {
  search?: string;
  status?: string | null;
  startDate?: Date | null;
  endDate?: Date | null;
  page?: number;
  size?: number;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
  schoolId?: string | null;
}

interface TableJobFamilyProps {
  schoolId?: string;
}

const TableJobFamily: React.FC<TableJobFamilyProps> = ({ schoolId }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();
  const [openedModalDelete, { open: openModalDelete, close: closeModalDelete }] =
    useDisclosure(false);
  const [openedViewModal, { open: openViewModal, close: closeViewModal }] = useDisclosure(false);
  const [columns, setColumns] = useState(COLUMN_JOB_FAMILY);
  const { setMode, setFormData } = useSchoolManagementStore();
  const [selectedRow, setSelectedRow] = useState<SchoolJobDataFamily | null>(null);
  const [params, setParams] = useState<JobFamilyParams>({ schoolId });

  const search = searchParams.get('search');
  const status = searchParams.get('status');
  const sortBy = searchParams.get('sortBy');
  const sortOrder = searchParams.get('sortOrder');
  const pageNumber = searchParams.get('page');
  const pageSize = searchParams.get('size');
  const startDate = searchParams.get('startDate');
  const endDate = searchParams.get('endDate');

  const { data, isLoading } = useQuery({
    queryKey: [
      'schoolJobFamily',
      search,
      status,
      sortBy,
      sortOrder,
      pageNumber,
      pageSize,
      startDate,
      endDate,
      schoolId
    ],
    queryFn: () =>
      schoolService.getSchoolJobFamilyList({
        search: search ?? undefined,
        startDate: startDate ?? undefined,
        endDate: endDate ?? undefined,
        status: status ?? undefined,
        sortBy: sortBy ?? undefined,
        sortOrder: (sortOrder as 'asc' | 'desc') ?? 'asc',
        pageNumber: Number(pageNumber) || 1,
        pageSize: Number(pageSize) || 10,
        schoolId: schoolId ?? undefined
      })
  });

  const deleteJobFamily = useMutation({
    mutationFn: schoolService.deleteSchoolJobFamily,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['schoolJobFamily'] });
      closeModalDelete();
      setSelectedRow(null);
      showSuccessNotification({
        title: 'Success',
        message: 'Job Family deleted successfully'
      });
    },
    onError: (error) => {
      showErrorNotification({
        title: 'Error when delete job family',
        message: error.message
      });
    }
  });

  useEffect(() => {
    const urlParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value) {
        if (value instanceof Date) {
          urlParams.set(key, value.toISOString());
        } else {
          urlParams.set(key, value.toString());
        }
      }
    });
    navigate(`${location.pathname}?${urlParams.toString()}`);
  }, [params, navigate, location.pathname]);

  const handleChangeSize = (size: number) =>
    setParams((prevState) => {
      return { ...prevState, size };
    });

  const handleChangePage = (page: number) =>
    setParams((prevState) => {
      return { ...prevState, page };
    });

  const onDelete = () => {
    return (
      selectedRow &&
      deleteJobFamily.mutate({
        TrSchoolJobFamilyID: selectedRow.trSchoolJobFamilyID
      })
    );
  };

  const handleEdit = (row: SchoolJobDataFamily) => {
    setMode('edit');
    setFormData(row);
    navigate(pathnames.editJobFamily.replace(':id', row.trSchoolJobFamilyID));
  };

  const handleDelete = (row: SchoolJobDataFamily) => {
    setSelectedRow(row);
    openModalDelete();
  };

  const handleView = (row: SchoolJobDataFamily) => {
    setSelectedRow(row);
    openViewModal();
  };

  const { data: jobFamilyDetail, isLoading: isLoadingDetail } = useQuery({
    queryKey: ['schoolJobFamilyDetail', selectedRow?.trSchoolJobFamilyID],
    queryFn: () =>
      selectedRow ? schoolService.getSchoolJobFamilyDetail(selectedRow.trSchoolJobFamilyID) : null,
    enabled: !!selectedRow?.trSchoolJobFamilyID && openedViewModal
  });

  const labelMapping: Record<string, string> = {
    dateUp: 'Last Updated Date',
    userUp: 'Last Updated By',
    trSchoolJobFamilyID: 'School Job Family ID',
    msSchoolCompetencyManagementID: 'School Competency Management ID',
    schoolName: 'School Name',
    jobFamilyID: 'Job Family ID',
    jobFamilyName: 'Job Family Name',
    dateIn: 'Created Date',
    userIn: 'Created By'
  };
  return (
    <>
      <Card>
        <Stack>
          <Toolbar
            columns={columns}
            showFilter={false}
            showFilterColumn
            setColumns={setColumns}
            createButtonText='New Job Family'
            buttonCreateLeftSection={<IconPlus />}
            onCreate={() => {
              setMode('add');
              setFormData({
                msSchoolCompetencyManagementID: '',
                jobFamily: []
              });
              navigate(pathnames.createJobFamily);
            }}
          />

          <DataTables
            columns={columns}
            data={data?.data || []}
            loading={isLoading}
            totalData={data?.totalRecords}
            currentPage={Number(params?.page)}
            currentSize={Number(params?.size)}
            withBulkData
            handleEdit={handleEdit}
            handleDelete={handleDelete}
            handleView={handleView}
            handleChangeSize={handleChangeSize}
            handleChangePage={handleChangePage}
            emptyMessageTitle='No Job Family Found'
            emptyMessageDescription='Please try again later'
          />
        </Stack>
      </Card>

      <ModalDelete
        title='Delete Job Family'
        description={`You are about to delete job family ${selectedRow?.jobFamilyName || 'this job family'}. Are you sure to delete it?`}
        labelConfirm='Delete'
        onClose={closeModalDelete}
        onConfirm={onDelete}
        opened={openedModalDelete}
        isLoading={deleteJobFamily.isPending}
      />

      <ModalPopup
        opened={openedViewModal}
        close={closeViewModal}
        title='Job Family Details'
        size='lg'
        withCloseButton
      >
        {isLoadingDetail ? (
          <Text>Loading job family details...</Text>
        ) : jobFamilyDetail ? (
          <Stack gap={16}>
            <Group gap={8}>
              <HiOutlineDocumentText />
              <Text c={'dark'} fw={700}>
                Detail Information
              </Text>
            </Group>

            {Object.keys(jobFamilyDetail?.data).map((key, index) => {
              if (key === 'dateIn' || key === 'dateUp') {
                return (
                  <Stack gap={0} key={index}>
                    <Text c={'dark'} fw={600} fz={14}>
                      {labelMapping[key]}
                    </Text>
                    {jobFamilyDetail?.data[key as any] ? (
                      <Dates
                        date={String(jobFamilyDetail?.data[key as any])}
                        dateFormat='dateNameTime'
                      />
                    ) : (
                      '-'
                    )}
                  </Stack>
                );
              }

              return (
                <Stack gap={0} key={index}>
                  <Text c={'dark'} fw={600} fz={14}>
                    {labelMapping[key]}
                  </Text>
                  <Text fz={14} c={'secondary'}>
                    {jobFamilyDetail?.data[key as any] || '-'}
                  </Text>
                </Stack>
              );
            })}
          </Stack>
        ) : (
          <Text>No job family details found</Text>
        )}
      </ModalPopup>
    </>
  );
};

export default TableJobFamily;
